/**
 * FeeChangePreview — shows what a file import or bulk action WOULD change, then applies it
 * all-or-nothing (#52, DEC-358 choice 10). Nothing is written until Apply; any row with an
 * error disables Apply. If someone changed a price in between, the API refuses the apply
 * and the user previews again.
 */

import React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  STATUS_LABELS,
  formatFee,
  type PreviewRow,
  type RowStatus,
} from './customer-disposal-fees';
import {
  useApplyImport,
  usePreviewImport,
  type PreviewRequest,
} from './customer-disposal-fees-hooks';
import { apiErrorText } from '@lodex-pro/web-shared';

const STATUS_COLORS: Record<RowStatus, string> = {
  ADD: 'var(--color-success)',
  CHANGE: 'var(--color-text-primary)',
  END: 'var(--color-warning)',
  UNCHANGED: 'var(--color-text-secondary)',
  ERROR: 'var(--color-danger)',
};

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export function FeeChangePreview(props: {
  readonly title: string;
  readonly request: PreviewRequest;
  readonly onDone: () => void;
}): React.JSX.Element {
  const preview = usePreviewImport();
  const apply = useApplyImport((r) => {
    const n = r.counts.add + r.counts.change + r.counts.end;
    toast.success(`${String(n)} price change${n === 1 ? '' : 's'} applied.`);
    props.onDone();
  });
  const [showUnchanged, setShowUnchanged] = React.useState(false);

  const { mutate } = preview;
  const run = React.useCallback((): void => {
    apply.reset();
    mutate(props.request);
    // apply.reset is stable; re-run only when the request changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutate, props.request]);
  React.useEffect(run, [run]);

  const data = preview.data;
  const counts = data?.counts;
  const changes = counts === undefined ? 0 : counts.add + counts.change + counts.end;
  const rows: PreviewRow[] =
    data === undefined
      ? []
      : data.rows.filter((r) => showUnchanged || r.status !== 'UNCHANGED' || r.warnings.length > 0);

  return (
    <section
      aria-label={props.title}
      style={{
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
        padding: '16px',
        marginBottom: '20px',
      }}
    >
      <h3 style={{ fontWeight: 600, marginBottom: '6px' }}>{props.title}</h3>
      {preview.isPending && (
        <p role="status" style={helpStyle}>
          Checking every row…
        </p>
      )}
      {preview.isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          {apiErrorText(preview.error, 'Could not preview this change. Try again.')}
        </p>
      )}
      {counts !== undefined && (
        <>
          <p style={{ marginBottom: '10px' }} data-testid="preview-counts">
            {String(counts.add)} to add · {String(counts.change)} to change · {String(counts.end)}{' '}
            to end · {String(counts.unchanged)} unchanged
            {counts.error > 0 && (
              <strong style={{ color: STATUS_COLORS.ERROR }}>
                {' '}
                · {String(counts.error)} with errors
              </strong>
            )}
          </p>
          {counts.error > 0 && (
            <p
              role="alert"
              style={{ ...helpStyle, color: STATUS_COLORS.ERROR, marginBottom: '10px' }}
            >
              Nothing will be applied until every error is fixed. Fix the file and upload it again.
            </p>
          )}
          <label style={{ ...helpStyle, display: 'flex', gap: '6px', marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={showUnchanged}
              onChange={(e): void => setShowUnchanged(e.target.checked)}
            />
            Show unchanged rows
          </label>
          <div style={{ maxHeight: '420px', overflow: 'auto', marginBottom: '12px' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Row</TableHead>
                  <TableHead>What</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Place</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.rowNumber}>
                    <TableCell>{r.rowNumber}</TableCell>
                    <TableCell style={{ color: STATUS_COLORS[r.status], fontWeight: 600 }}>
                      {STATUS_LABELS[r.status]}
                    </TableCell>
                    <TableCell>{r.customerLabel ?? '—'}</TableCell>
                    <TableCell>{r.siteName ?? '—'}</TableCell>
                    <TableCell>{r.effectiveFrom ?? '—'}</TableCell>
                    <TableCell>
                      {r.status === 'ERROR'
                        ? ''
                        : r.status === 'UNCHANGED'
                          ? formatFee(r.before)
                          : `${formatFee(r.before)} → ${r.after === null ? 'default / none' : formatFee(r.after)}`}
                    </TableCell>
                    <TableCell style={{ fontSize: '13px' }}>
                      {r.errors.map((e) => (
                        <div key={e} style={{ color: STATUS_COLORS.ERROR }}>
                          {e}
                        </div>
                      ))}
                      {r.warnings.map((w) => (
                        <div key={w} style={{ color: STATUS_COLORS.END }}>
                          {w}
                        </div>
                      ))}
                      {r.errors.length === 0 && r.notes !== null && <div>{r.notes}</div>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
      {apply.isError && (
        <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: '10px' }}>
          {apiErrorText(apply.error, 'Could not save this change.')}{' '}
          <Button variant="outline" size="sm" onClick={run}>
            Preview again
          </Button>
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          disabled={
            data === undefined ||
            counts === undefined ||
            counts.error > 0 ||
            changes === 0 ||
            apply.isPending
          }
          onClick={(): void => {
            if (data === undefined) return;
            apply.mutate({
              ...props.request,
              issuedAt: data.issuedAt,
              previewToken: data.previewToken,
            });
          }}
        >
          {apply.isPending
            ? 'Applying…'
            : `Apply ${String(changes)} change${changes === 1 ? '' : 's'}`}
        </Button>
        <Button variant="outline" onClick={props.onDone}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
